import styled, { css } from "styled-components";
import StatusDot from "features/admin/NewLook/StatusDot";
import { getMemberStatusColor } from "features/admin/NewLook/statusColors";
import { find } from "lodash";
import { STATUSES_INFO_MAP } from "features/admin/group/users/useFilters";
import { StatusWrapper } from "features/admin/group/users/styles";
import { sortByStatus } from "features/admin/group/users/UsersTable";
import { StatusCircle } from "Global/StatusCircle";
import Table from "common/Table/Table";
import { sortByStringProperty } from "@shared/utils/general";
import { formatDate, sortRawDates } from "@shared/utils/date";
import React from "react";
import Text from "common/Typography/Text";
import { useTheme } from "styled-components";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useBottomNavigator } from "utils/hooks/misc";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

const StyledTable = styled(Table)`
  table {
    border: 1px solid #f0f0f0;
    border-bottom: 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* New look: a plain list in the well, no box around it (&&& beats
         the panel table's row rules around it). */
      table {
        border: 0;
      }
      .ant-table {
        background: transparent;
      }
      &&& .ant-table-thead > tr > th {
        padding: 8px 12px;
      }
      &&& .ant-table-tbody > tr > td {
        height: 44px;
        padding: 6px 12px;
        background: transparent !important;
      }
      &&& .ant-table-thead > tr > th:first-child,
      &&& .ant-table-tbody > tr > td:first-child {
        padding-left: 0;
      }
    `}
`;

const InvitationUsers = ({
  users,
  wrapperRef,
  telegramIdToMembershipMapping,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const appTheme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const columns = [
    {
      title: t("users.table.firstName"),
      dataIndex: "telegram_user",
      key: "first_name",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "telegram_user.first_name"),
      render: (telegramUser, _) => telegramUser && telegramUser.first_name,
    },
    {
      title: t("users.table.lastName"),
      dataIndex: "telegram_user",
      key: "last_name",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "telegram_user.last_name"),
      render: (telegramUser, _) => telegramUser && telegramUser.last_name,
    },
    {
      title: t("users.table.username"),
      dataIndex: "telegram_user",
      key: "username",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "telegram_user.username"),
      render: (telegramUser, _) => telegramUser && telegramUser.username,
    },
    {
      title: t("users.table.telegramId"),
      dataIndex: "telegram_user_id",
      key: "telegram_user_id",
    },
    {
      title: t("users.table.expirationDate"),
      dataIndex: "telegram_user_id",
      key: "expiration_datetime",
      showSorterTooltip: false,
      render: (telegram_user_id, _) => (
        <Text style={{ color: appTheme.colors.textColor }}>
          {formatDate(
            telegramIdToMembershipMapping[telegram_user_id].expiration_datetime
          )}
        </Text>
      ),
      sorter: (a, b) =>
        sortRawDates(
          telegramIdToMembershipMapping[a.telegram_user_id].expiration_datetime,
          telegramIdToMembershipMapping[b.telegram_user_id].expiration_datetime
        ),
    },
    {
      title: t("users.table.status"),
      dataIndex: "status",
      key: "status",
      defaultSortOrder: "descend",
      render: (status, _) => {
        const displayInfo = find(
          STATUSES_INFO_MAP,
          (statusesInfo) => statusesInfo.statuses.indexOf(status) > -1
        );
        if (appTheme.isNewLook)
          return (
            <StatusDot
              color={getMemberStatusColor(appTheme, displayInfo?.displayName)}
            >
              {t(displayInfo.displayName)}
            </StatusDot>
          );
        return (
          <StatusWrapper>
            <StatusCircle color={displayInfo.color} />
            <span>{t(displayInfo.displayName)}</span>
          </StatusWrapper>
        );
      },
      sorter: (a, b) => sortByStatus(a.status, b.status),
    },
  ];

  // New look (phones): the accepted members as short rows (name over the
  // handle and expiration, status on the right), not a nested table.
  if (appTheme.isNewLook && isBottomNavigator)
    return (
      <PhoneList ref={wrapperRef}>
        {users.map((user, index) => {
          const displayInfo = find(
            STATUSES_INFO_MAP,
            (statusesInfo) => statusesInfo.statuses.indexOf(user.status) > -1
          );
          const name = [
            user.telegram_user?.first_name,
            user.telegram_user?.last_name,
          ]
            .filter(Boolean)
            .join(" ");
          const expiration =
            telegramIdToMembershipMapping[user.telegram_user_id]
              ?.expiration_datetime;
          return (
            <PhoneRow key={index}>
              <PhoneNames>
                <PhoneName>{name || user.telegram_user_id}</PhoneName>
                <PhoneMeta>
                  {[
                    user.telegram_user?.username &&
                      `@${user.telegram_user.username}`,
                    expiration && formatDate(expiration),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </PhoneMeta>
              </PhoneNames>
              {displayInfo && (
                <StatusDot
                  color={getMemberStatusColor(
                    appTheme,
                    displayInfo.displayName
                  )}
                >
                  {t(displayInfo.displayName)}
                </StatusDot>
              )}
            </PhoneRow>
          );
        })}
      </PhoneList>
    );

  return (
    <div
      style={{ padding: appTheme.isNewLook ? "8px 20px 12px 64px" : "24px" }}
      ref={wrapperRef}
    >
      <StyledTable
        layout="fixed"
        columns={columns}
        dataSource={users.map((user, i) => ({ ...user, key: i }))}
      />
    </div>
  );
};

export default InvitationUsers;

const PhoneList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 16px 8px 48px;
`;
const PhoneRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 52px;
  padding: 8px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const PhoneNames = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const PhoneName = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const PhoneMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
